// Intent-bridge shared logic — the single source of truth for how an ad/intent
// visitor is routed and how an intent seeds the first ChitraGupt question.
//
// Historically this lived inline in PremiumDashboard's intent-bridge effect,
// which only fired AFTER the dashboard mounted — so ad users flashed the
// dashboard (and its app-download CTA) before reaching ChitraGupt, and a
// generic/lead-only arrival never left the dashboard at all. Extracting it here
// lets onboarding route straight to the /chitragupt entry card (no flash) while
// the dashboard effect keeps working for users who reach the dashboard by other
// paths — both reading the same map so they can never drift.

// Intent → the question we pre-seed for the first ChitraGupt message. Keep in
// sync with the backend's understanding of these three intents.
export const INTENT_PROMPTS = {
  career:   'Analyze my 10th house and current transits for career growth.',
  health:   'Give me a health and vitality overview based on my 6th house and Ascendant.',
  marriage: 'What does my chart indicate about my future spouse and marriage timing?',
};

// Seed the pending chat question for an intent so the eventual session opens on
// it. No-op (returns null) for intents we have no prompt for — the entry card
// still renders, just without a pre-filled question.
export function seedIntentChat(intent) {
  const prompt = INTENT_PROMPTS[intent];
  if (!prompt) return null;
  try {
    sessionStorage.setItem('pendingChatQuestion', prompt);
    localStorage.setItem('pending_chat_message', JSON.stringify({ text: prompt, createdAt: Date.now() }));
  } catch { /* storage blocked — non-fatal, the session just starts blank */ }
  return prompt;
}

// "Fresh trial wallet" = a brand-new signup whose wallet holds ONLY the trial
// karma credited at signup (purchased_balance 0, total_balance > 0), who has
// never had a session, whose onboarding is complete and who has birth details.
// Kept as one pure predicate so BOTH the onboarding router and the post-login
// landing gate on the same rule (invariant: "new user" is keyed on wallet state
// + onboarding_complete + birth details, NEVER on the trial-activation call —
// trial karma is credited at signup, before onboarding, per mobile #646/#647).
export function isFreshTrialWallet({
  purchasedBalance = null,
  totalBalance = null,
  sessionCount = null,
  onboardingComplete = false,
  hasBirthDetails = false,
} = {}) {
  // Any unresolved signal (null) fails closed — never route a user to the
  // free-first entry card on incomplete data.
  if (purchasedBalance === null || totalBalance === null || sessionCount === null) return false;
  return (
    Number(purchasedBalance) === 0 &&
    Number(totalBalance) > 0 &&
    Number(sessionCount) === 0 &&
    !!onboardingComplete &&
    !!hasBirthDetails
  );
}

// Per-USER (never per-browser) key for the one-time free-first entry nudge.
// Including the user_id is what makes "show once per user" honest: a shared
// browser must still nudge each new signup exactly once.
export function entryNudgeKey(userId) {
  return `jp_entry_nudge_shown:${userId}`;
}

// Dashboard card order for a pre-session seeker: the ChitraGupt entry CTA sits
// ABOVE the Daily Prasad card until the first session, then the normal order
// (Prasad = the retention loop) is restored. Payers keep the normal order — no
// layout change. Fails closed while sessionCount is unknown (null) so the order
// never flickers before the count resolves.
export function showEntryCtaAbovePrasad({ sessionCount = null, isPayer = false } = {}) {
  return sessionCount === 0 && !isPayer;
}

// Pure resolver: given the incoming URL/stash signals, decide where the visitor
// should land. Kept side-effect-free so it is unit-testable in isolation.
//   - matchmaking intent            → the compatibility (Guna Milan) product
//   - any other intent, or a lead_id → the /chitragupt entry card (intent_bridge)
//   - a fresh trial wallet (no intent/lead) → the /chitragupt entry card (new_user)
//   - nothing                        → the dashboard (default home)
// Matchmaking always keeps its Guna Milan route, even for a fresh trial wallet.
export function resolveEntryDestination({ intentParam = '', leadId = '', stashedIntent = '', freshTrialWallet = false } = {}) {
  const intent = String(intentParam || stashedIntent || '').toLowerCase();
  const hasLead = !!leadId;
  if (intent === 'matchmaking') return { view: 'compatibility', intent, source: 'intent_bridge' };
  if (intent) return { view: 'chat', intent, source: 'intent_bridge' };
  if (hasLead) return { view: 'chat', intent: null, source: 'intent_bridge' };
  if (freshTrialWallet) return { view: 'chat', intent: null, source: 'new_user' };
  return { view: 'dashboard', intent: null, source: null };
}
